<template>
	<div class="calculator calculator-mortgage-affordability">
		<div class="container p-0">
			<div class="row flex-row calculator__row">
				<div class="calculator__form col-lg-6">
					<h2
						class="text-start"
						v-html="microcopy.heading"
					/>

					<form @submit.prevent="updateResults()">
						<RadioBtnList :label="microcopy.numberOfApplicants.label">
							<RadioBtn
								:key="option.value"
								v-for="option in [
									{
										label: microcopy.numberOfApplicants.buttonOneLabel,
										value: '1'
									},
									{
										label: microcopy.numberOfApplicants.buttonTwoLabel,
										value: '2'
									}
								]"
								required
								name="number-of-applicants"
								:label="option.label"
								:value="option.value"
								:checked="option.value === `${formData.applicants}`"
								@input="() => {
									/** @type {Partial<MtgeAffordabilityFormData>} */
									const payload = {
										applicants: parseInt(option.value)
									};

									if (payload.applicants === 1) {
										payload.appTwoIncome = null;
									}

									set(payload);
								}"
							/>
						</RadioBtnList>

						<TextInputField
							v-if="formData.applicants === 1 || formData.applicants === 2"
							required
							input-type="currency"
							:maximum-value="microcopy.maximumInputValue"
							:label-text="microcopy.applicantOneIncome.label"
							error-text="Applicant 1: Please enter your annual income before tax"
							@field-input="(value) => {
								set({
									appOneIncome: value
								});
							}"
						/>

						<TextInputField
							v-if="formData.applicants === 2"
							required
							input-type="currency"
							:maximum-value="microcopy.maximumInputValue"
							:label-text="microcopy.applicantTwoIncome.label"
							error-text="Applicant 2: Please enter your annual income before tax"
							@field-input="(value) => {
								set({
									appTwoIncome: value
								});
							}"
						/>

						<TextInputField
							required
							input-type="currency"
							:minimum-value="1"
							:maximum-value="microcopy?.maximumInputValue"
							:label-text="microcopy?.propertyValue?.label"
							:error-text="microcopy?.propertyValue?.validationMessages?.empty"
							@field-input="(value) => {
								set({
									propertyValue: value
								});
							}"
						>
							<template v-slot:tooltip>
								<Tooltip :content="microcopy?.propertyValue?.tooltip" />
							</template>
						</TextInputField>


						<TextInputField
							required
							input-type="currency"
							:maximum-value="parseFloat(formData.propertyValue?.value) || microcopy.maximumInputValue"
							:label-text="microcopy.depositAmount.label"
							:error-text="!formData.deposit?.value
								? microcopy.depositAmount.validationMessages.empty
								: microcopy.depositAmount.validationMessages.exceedsPropertyValue"
							@field-input="(value) => {
								set({
									deposit: value
								});
							}"
							:manualInputDirtier="(formData.propertyValue?.value < formData.deposit?.value)"
						>
							<template v-slot:tooltip>
								<Tooltip :content="microcopy?.depositAmount?.tooltip" />
							</template>
						</TextInputField>


						<button
							class="btn btn-primary calculator__form-submit-btn"
							type="submit"
							:disabled="!valid"
							v-html="!resultsDataSet
								? microcopy.calculateButtonLabel
								: microcopy.recalculateButtonLabel"
						/>
					</form>
				</div>

				<!-- results -->

				<div class="results-container d-none d-lg-block col-lg-6 p-0">
					<!-- desktop results -->
					<div class="row">
						<div class="col">
							<div class="results">
								<h3
									class="text-start"
									v-html="microcopy.resultsPanel.heading"
								/>

								<hr class="hr--small" />

								<div
									v-show="resultsDataSet"
									class="d-lg-block"
								>
									<div class="col text-center text-lg-start">
										<p>
											<span
												class="results-label"
												v-html="microcopy.resultsPanel.borrowUpToLabel"
											/>
											<br />
											<span class="results-amount">
												{{ formatPrice(resultsData.estimatedMtge) }}
											</span>
										</p>
									</div>
								</div>

								<hr class="hr--small" />

								<div class="row">
									<div class="col text-center text-lg-start">
										<p>
											<span
												class="results-label"
												v-html="microcopy.resultsPanel.borrowAmountLabel"
											/>
											<br />
											<span class="results-amount">
												{{ formatPrice(resultsData.desiredMtge) }}
											</span>
										</p>
									</div>
								</div>

								<hr class="hr--small" />

								<div class="row">
									<div class="col text-center text-lg-start">
										<p>
										<div class="row flex-nowrap has-tooltip tooltip-inline">
											<span
												class="col-auto results-label"
												v-html="microcopy.resultsPanel.ltvLabel"
											/>
											<Tooltip :content="microcopy.resultsPanel.ltvTooltip" />
										</div>
										<span class="results-amount">
											{{ formatPercentage(resultsData.ltv) }}
										</span>
										</p>
									</div>
								</div>

								<hr class="hr--small" />

								<div class="row">
									<div class="col text-start">
										<h3
											class="results-heading"
											v-html="microcopy.resultsPanel.ctaTitle"
										/>

										<div
											class="results-body"
											v-html="microcopy.resultsPanel.ctaParagraph"
										/>

										<p>
											<a
												class="arrow-btn arrow-btn--white"
												:href="microcopy.resultsPanel.ctaOneLink.href"
												:target="microcopy.resultsPanel.ctaOneLink.target"
												v-html="microcopy.resultsPanel.ctaOneLink.text"
											/>
										</p>

										<p>
											<a
												class="arrow-btn arrow-btn--white"
												:href="microcopy.resultsPanel.ctaTwoLink.href"
												:target="microcopy.resultsPanel.ctaTwoLink.target"
												v-html="microcopy.resultsPanel.ctaTwoLink.text"
											/>
										</p>
									</div>
								</div>
							</div>

							<div
								v-show="!resultsDataSet"
								class="d-lg-none"
								v-html="microcopy.resultsPanel.defaultText"
							/>
						</div>
					</div>
				</div>

				<!-- mobile results -->

				<div class="results-container-mobile d-block d-lg-none col-12 p-0 order-last results-container">
					<div class="container">
						<div class="row">
							<div class="col results mobile-results">
								<div
									v-show="resultsDataSet"
									class="d-lg-block"
								>
									<div class="row">
										<div class="col text-center text-lg-start">
											<p>
												<span
													class="results-label"
													v-html="microcopy.resultsPanel.borrowUpToLabel"
												/>
												<br />
												<span class="results-amount">
													{{ formatPrice(resultsData.estimatedMtge) }}
												</span>
											</p>
										</div>
									</div>
									<hr class="hr--small" />

									<div class="row">
										<div class="col text-center text-lg-start">
											<p>
												<span
													class="results-label"
													v-html="microcopy.resultsPanel.borrowAmountLabel"
												/>
												<br />
												<span class="results-amount">
													{{ formatPrice(resultsData.desiredMtge) }}
												</span>
											</p>
										</div>
									</div>

									<hr class="hr--small" />

									<div class="row">
										<div class="col text-center text-lg-start">
											<p>
											<div class="row flex-nowrap justify-content-center has-tooltip tooltip-inline">
												<span
													class="col-auto results-label"
													v-html="microcopy.resultsPanel.ltvLabel"
												/>
												<Tooltip :content="microcopy.resultsPanel.ltvTooltip" />
											</div>

											<span class="results-amount">
												{{ formatPercentage(resultsData.ltv) }}
											</span>
											</p>
										</div>
									</div>

									<hr class="hr--gold-2 hr--small mx-minus-20">

									<div class="row">
										<div class="col text-start">
											<h3
												class="results-heading"
												v-html="microcopy.resultsPanel.ctaTitle"
											/>

											<div
												class="results-body"
												v-html="microcopy.resultsPanel.ctaParagraph"
											/>

											<p>
												<a
													class="arrow-btn arrow-btn--white"
													:href="microcopy.resultsPanel.ctaOneLink.href"
													:target="microcopy.resultsPanel.ctaOneLink.target"
													v-html="microcopy.resultsPanel.ctaOneLink.text"
												/>
											</p>

											<p>
												<a
													class="arrow-btn arrow-btn--white"
													:href="microcopy.resultsPanel.ctaTwoLink.href"
													:target="microcopy.resultsPanel.ctaTwoLink.target"
													v-html="microcopy.resultsPanel.ctaTwoLink.text"
												/>
											</p>
										</div>
									</div>
								</div>

								<div
									v-show="!resultsDataSet"
									class="d-lg-none"
									v-html="microcopy.resultsPanel.defaultText"
								/>
							</div>
						</div>
					</div>
				</div>

				<!-- notification -->

				<div
					class="calculator__notification col"
					v-html="microcopy.complianceMessage"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import helper from '../mixins/helpers.js';
import RadioBtnList from '../shared/components/RadioBtnList.vue';
import RadioBtn from '../shared/components/RadioBtn.vue';
import TextInputField from '../shared/components/TextInputField.vue';
import Tooltip from '../shared/components/Tooltip.vue';

import { formatPrice, formatPercentage } from '../utils';

const microcopy = window.mortgageAffordabilityCalcMicrocopy;

export default {
	components: {
		RadioBtnList,
		RadioBtn,
		TextInputField,
		Tooltip,
	},
	mixins: [
        helper
    ],
	data() {
		return {
			microcopy,
			/** @type {MtgeAffordabilityFormData} */
			formData: {
				applicants: 1,
				appOneIncome: null,
				appTwoIncome: null,
				propertyValue: null,
				deposit: null
			},
			/** @type {MtgeAffordabilityResultsData} */
			resultsData: {
				estimatedMtge: 0,
				desiredMtge: 0,
				ltv: 0
			},
			resultsDataSet: false
		};
	},
	computed: {
		/**
		 * The validity of the form.
		 */
		valid() {
			return this.formData.applicants !== null
				&& this.formData.appOneIncome?.isValid
				&& (
					(
						this.formData.applicants === 1
						&& this.formData.appTwoIncome === null
					)
					|| (
						this.formData.applicants === 2
						&& this.formData.appTwoIncome?.isValid
					)
				)
				&& this.formData.propertyValue?.isValid
				&& this.formData.deposit?.isValid
				|| false;
		}
	},
	methods: {
		/**
		 * Sets the form data.
		 * @param payload {Partial<MtgeAffordabilityFormData>}
		 */
		set(payload) {
			this.formData = {
				...this.formData,
				...payload
			};
		},

		/**
		 * Updates the results data.
		 */
		updateResults() {
			if (!this.valid) {
				return;
			}

			const
				appOneIncome = parseFloat(this.formData.appOneIncome.value),
				appTwoIncome = parseFloat(this.formData.appTwoIncome?.value ?? '0'),
				propertyValue = parseFloat(this.formData.propertyValue.value),
				deposit = parseFloat(this.formData.deposit.value);

			this.resultsData = {
				estimatedMtge: (appOneIncome + appTwoIncome) * 4.5,
				desiredMtge: propertyValue - deposit,
				ltv: (propertyValue - deposit) / propertyValue
			};

			this.resultsDataSet = true;
            this.scrollToVisibleResults();
		},

		formatPrice,
		formatPercentage
	}
};

/**
 * @typedef {{
 *   applicants: 1 | 2 | null;
 *   appOneIncome: TextInputData | null;
 *   appTwoIncome: TextInputData | null;
 *   propertyValue: TextInputData | null;
 *   deposit: TextInputData | null;
 * }} MtgeAffordabilityFormData
 */

/**
 * @typedef {{
 *   estimatedMtge: number;
 *   desiredMtge: number;
 *   ltv: number;
 * }} MtgeAffordabilityResultsData
 */

/**
 * @typedef {import('../types/window')} Content
 */

/**
 * @typedef {import('../shared/components/TextInputField.vue').TextInputData} TextInputData
 */

</script>
